import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import AppShell from "./components/app-shell";
import { serverFetch } from "./core/server";
import AuthPage from "./pages/auth-page";
import CabinetPage from "./pages/cabinet-page";
import CompaniesPage from "./pages/companies-page";
import DefaultAppPage from "./pages/default-app-page";
import HowToStartPage from "./pages/how-to-start-page";
import LandingPage from "./pages/landing-page";
import SuperAdminsPage from "./pages/superadmins-page";
import { sessionFromApi } from "./store/session";
import { clearUserSession, setUserSession } from "./store/user-slice";

const RequireAuth = ({ children }) => {
  const session = useSelector((state) => state.user.session);
  if (!session) {
    return <Navigate to="/auth" replace />;
  }
  return children;
};

const RequireSuperAdmin = ({ children }) => {
  const session = useSelector((state) => state.user.session);
  if (!session) {
    return <Navigate to="/auth" replace />;
  }
  if (!session.isSuperAdmin) {
    return <Navigate to="/cabinet" replace />;
  }
  return children;
};

const AuthRoute = () => {
  const session = useSelector((state) => state.user.session);
  if (session) {
    return <Navigate to="/cabinet" replace />;
  }
  return <AuthPage />;
};

const App = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const session = useSelector((state) => state.user.session);
  const [blockingSessionCheck, setBlockingSessionCheck] = useState(
    () => !!localStorage.getItem("sessionId")
  );

  useEffect(() => {
    if (location.pathname === "/start") {
      document.title = "Как начать работу — ВЕБ Окна";
      return;
    }
    document.title = session ? "Кабинет — ВЕБ Окна" : "ВЕБ Окна — личный кабинет";
  }, [session, location.pathname]);

  useEffect(() => {
    const sessionId = localStorage.getItem("sessionId");
    if (!sessionId) {
      setBlockingSessionCheck(false);
      return undefined;
    }

    let cancelled = false;
    serverFetch("auth/checksession")
      .then((data) => {
        if (cancelled) return;
        dispatch(setUserSession(sessionFromApi(data, sessionId)));
      })
      .catch(() => {
        if (cancelled) return;
        localStorage.removeItem("sessionId");
        dispatch(clearUserSession());
      })
      .finally(() => {
        if (!cancelled) setBlockingSessionCheck(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  if (blockingSessionCheck) {
    return null;
  }

  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/start" element={<HowToStartPage />} />
        <Route path="/auth" element={<AuthRoute />} />
        <Route
          path="/cabinet"
          element={
            <RequireAuth>
              <CabinetPage />
            </RequireAuth>
          }
        />
        <Route
          path="/cabinet/default"
          element={
            <RequireSuperAdmin>
              <DefaultAppPage />
            </RequireSuperAdmin>
          }
        />
        <Route
          path="/cabinet/companies"
          element={
            <RequireSuperAdmin>
              <CompaniesPage />
            </RequireSuperAdmin>
          }
        />
        <Route
          path="/cabinet/superadmins"
          element={
            <RequireSuperAdmin>
              <SuperAdminsPage />
            </RequireSuperAdmin>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
};

export default App;
